import * as React from 'react';
import { FormatDate, formatFieldValue, formatValue } from '../../utils/common';
import { Resource, ResourceEntityState, ResourceStatus, createResourceCalendar, findUsage } from '../../../store/ResourcesListStore';
import { PersonaSize, Icon } from 'office-ui-fabric-react';
import { ApplicationState } from '../../../store';
import { connect } from 'react-redux';
import { calculateSummary, ISummaryInfo } from './ResourceUsageCell';
import { ImagePersonaCoin } from '../../common/ImagePersona';
import { Dictionary, EntityType, entityTypeLabelMap, mapServerEntityType } from '../../../entities/common';
import { FormatType } from '../../../entities/Metadata';
import { ResourceBookingType, calculateStateInEntity } from './ResourceUsageGrid';
import { StatusView } from '../../views/list/columns/resource/Status';
import { StateInEntityView } from '../../views/list/columns/resource/StateInEntity';
import { TenantState } from '../../../store/Tenant';
import { UserState } from '../../../store/User';
import { getDefaultCostRateTable } from '../../../store/selectors/costRateTableSelectors';
import { CalendarDataSet } from '../../../store/calendar/common';
import { getEntityCalendar } from '../../../store/CalendarsListStore';
import { WorkByEntity } from './WorkByEntity';

type StateProps = {
    baseResourceCalendar: CalendarDataSet;
    resourcePlanningLevel: EntityType;
    tenant: TenantState;
    user: UserState;
    defaultCostRateTableId?: string;
}

export type OwnProps = {
    resource: Resource;
    startDate: Date;
    finishDate: Date;
    entityId?: string;
    entityType: EntityType;
    onMenuRender?: () => JSX.Element;
    showPerEntityWork?: boolean;
};

type Props = OwnProps & StateProps;
type State = { summary: ISummaryInfo };

class UtilizationTooltipContent extends React.Component<Props, State> {
    constructor(props: Props) {
        super(props);
        const { resource, startDate, finishDate, baseResourceCalendar, entityType, resourcePlanningLevel, defaultCostRateTableId } = props;
        const summary = calculateSummary(resource, startDate, finishDate, baseResourceCalendar, entityType, resourcePlanningLevel, defaultCostRateTableId);
        this.state = {
            summary
        };
    }

    componentWillReceiveProps(nextProps: Props) {
        const { resource, startDate, finishDate, baseResourceCalendar, entityType, resourcePlanningLevel, defaultCostRateTableId } = nextProps;
        this.setState({ summary: calculateSummary(resource, startDate, finishDate, baseResourceCalendar, entityType, resourcePlanningLevel, defaultCostRateTableId) });
    }

    render() {
        const { resource, startDate, finishDate, entityId, baseResourceCalendar, entityType, tenant, user } = this.props;
        const { summary } = this.state;
        const { capacityHours, availability, overage, hasLocalOverage, committedByEntity, proposedByEntity, committedWork, proposedWork, actualWork,
            proposed_overage, proposed_hasLocalOverage, proposed_overage_byEntity, proposed_hasLocalOverage_byEntity } = summary;
        const proposedOverage = entityId ? (proposed_overage_byEntity[entityId] ?? overage) : proposed_overage;
        const hasLocalProposedOverage = entityId ? proposed_hasLocalOverage_byEntity[entityId] : proposed_hasLocalOverage;

        const isSingleDay = startDate.getBeginOfDay().getTime() == finishDate.getBeginOfDay().getTime();
        const resourceCalendar = createResourceCalendar(resource, baseResourceCalendar);
        const calendarException = isSingleDay && resourceCalendar.exceptionsHoursMap[startDate.getBeginOfDay().getTime()] !== undefined
            ? resourceCalendar.exceptions.find(_ => _.start <= startDate && startDate <= _.end && _.days.includes(startDate.getDay()))
            : undefined;

        const effectiveUsage = entityId
            ? (summary.effectiveUsageByEntity[entityId] ?? summary.effectiveUsage)
            : summary.effectiveUsage;
        const effectiveStandardRate = effectiveUsage?.standardRate;
        const effectiveStandardCost = effectiveUsage?.standardCost;
        const effectiveMaxUnitsPct = effectiveUsage?.maxUnitsPct;
        const effectiveNonProjectWorkPct = effectiveUsage?.nonProjectWorkPct;

        const status = resource.attributes.Status;
        const stateInEntity = entityId ? calculateStateInEntity([resource], [entityId]) : undefined;
        const isInactive = status === ResourceStatus.Inactive || stateInEntity === ResourceEntityState.Inactive;

        const estimatedCharge = entityId ? (summary.estimatedChargeByEntity[entityId] ?? 0) : summary.estimatedCharge;
        const estimatedCost = entityId ? (summary.estimatedCostByEntity[entityId] ?? 0) : summary.estimatedCost;
        const actualCost = entityId ? (summary.actualCostByEntity[entityId] ?? 0) : summary.actualCost;

        return (
            <div className="timeline-tooltip">
                <div className="header">
                    <span className={isInactive ? 'inactive-persona' : undefined}>
                        <ImagePersonaCoin className="logo" size={PersonaSize.size32} info={resource} text={resource.name} />
                        {isInactive && <span className="inactive-icon" />}
                    </span>
                    <div className="title">
                        <div className="overflow-text" title={resource.name}>
                            {resource.name}
                        </div>
                    </div>
                    {!!entityId && <ResourceBookingType resource={resource} entityId={entityId} />}
                </div>
                <div className="content">
                    {status !== undefined && this.renderField("State", <StatusView status={status} />)}
                    {!!entityId && stateInEntity !== undefined
                        && this.renderField(`State in ${entityTypeLabelMap[entityType].singular}`, <StateInEntityView state={stateInEntity} />)}
                    {isSingleDay
                        ? this.renderField("Date", <div className="no-overflow ellipsis">
                            {FormatDate(startDate, { weekday: 'long' })}
                            {calendarException ? <> - <span title={calendarException.name}>{calendarException.name}</span></> : ''}
                        </div>)
                        : this.renderField("Period", <div>{FormatDate(startDate)} - {FormatDate(finishDate)}</div>)}
                    {this.renderField("Availability",
                        <div><span className={`progress-tip ${availability > 0 ? '' : 'gray'}`}>{formatValue(availability)}</span> hour{availability !== 1 ? 's' : ''}</div>)}
                    {this.renderField("Capacity", <div>{formatValue(capacityHours)} hour{capacityHours !== 1 ? 's' : ''}</div>)}
                    {isSingleDay && this.renderField("Max Units", <div>
                        {effectiveMaxUnitsPct !== undefined ? formatValue(effectiveMaxUnitsPct, FormatType.Percent) : <span className="non-completed">N/A</span>}
                    </div>)}
                    {isSingleDay && this.renderField("Non-Project Work", <div>
                        {effectiveNonProjectWorkPct !== undefined ? formatValue(effectiveNonProjectWorkPct, FormatType.Percent) : <span className="non-completed">N/A</span>}
                    </div>)}
                    {isSingleDay && this.renderField("Standard Rate", <div>
                        {effectiveStandardRate !== undefined ? formatFieldValue(effectiveStandardRate, FormatType.Cost, user, entityType, tenant) : <span className="non-completed">N/A</span>}
                    </div>)}
                    {isSingleDay && this.renderField("Standard Cost", <div>
                        {effectiveStandardCost !== undefined ? formatFieldValue(effectiveStandardCost, FormatType.Cost, user, entityType, tenant) : <span className="non-completed">N/A</span>}
                    </div>)}
                    {entityId
                        ? this.renderEntityWork()
                        : <>
                            {this.renderAllWork("Committed", committedWork, committedByEntity)}
                            {this.renderAllWork("Proposed", proposedWork, proposedByEntity)}
                        </>}
                    {this.renderField("Estimated Charge", <div>{formatFieldValue(estimatedCharge, FormatType.Cost, user, entityType, tenant, "EstimatedCharge")}</div>)}
                    {this.renderField("Estimated Cost", <div>{formatFieldValue(estimatedCost, FormatType.Cost, user, entityType, tenant, "EstimatedCost")}</div>)}
                    {this.renderField("Actual Cost", <div>{formatFieldValue(actualCost, FormatType.Cost, user, entityType, tenant, "ActualCost")}</div>)}
                    {this.renderField("Actual", <div>{formatValue(actualWork)} hour{actualWork !== 1 ? 's' : ''}</div>)}
                    {this.renderField("Overallocated", <div className='align-top'>
                        {overage !== 0
                            ? <div><span className="progress-tip red">{formatValue(overage)}</span>
                                {proposedOverage === overage ? ` hour${overage !== 1 ? 's' : ''}` : undefined}</div>
                            : hasLocalOverage
                                ? <div><Icon iconName="Warning" className="warning-icon" title="" />Some days</div>
                                : <div className="non-completed">n/a</div>}
                        {proposedOverage !== overage
                            ? <div>
                                &nbsp;/ <span className="progress-tip red">{formatValue(proposedOverage)}</span> hour{overage > 0 || proposedOverage > 0 ? 's' : ''} (if committed)
                            </div>
                            : !hasLocalOverage && hasLocalProposedOverage
                                ? <div>
                                    &nbsp;/ <Icon iconName="Warning" className="warning-icon" />Some days (if committed)
                                </div>
                                : undefined}
                    </div>)}
                    {!!this.props.onMenuRender && this.renderField("", this.props.onMenuRender(), 'menu')}
                </div>
            </div>
        );
    }

    private renderEntityWork = () => {
        const { entityId, showPerEntityWork, resource, entityType, resourcePlanningLevel } = this.props;
        const { summary } = this.state;
        const { committedByEntity, proposedByEntity } = summary;
        const committed = findUsage(resource.usage, entityId, _ => _.committed);
        const hours = (committed ? committedByEntity : proposedByEntity)[entityId!] ?? 0;
        const otherCommittedByEntity = Object.keys(committedByEntity)
            .filter(_ => _ !== entityId)
            .reduce<Dictionary<number>>((cum, _) => ({ ...cum, [_]: committedByEntity[_] }), {});

        return <>
            {this.renderField("Allocated",
                <div><span className="progress-tip gray">{formatValue(hours)}</span> hour{hours !== 1 ? 's' : ''} on this {entityType}</div>)}
            {showPerEntityWork && this.renderField("Other Work",
                <WorkByEntity hoursByEntity={otherCommittedByEntity} entityType={resourcePlanningLevel} />, 'align-top')}
        </>;
    }

    private renderAllWork = (label: string, hours: number, hoursByEntity: Dictionary<number>) => {
        const { showPerEntityWork, resourcePlanningLevel } = this.props;

        return this.renderField(label, showPerEntityWork
            ? <WorkByEntity hoursByEntity={hoursByEntity} entityType={resourcePlanningLevel} />
            : <div><span className="progress-tip gray">{formatValue(hours)}</span> hour{hours !== 1 ? 's' : ''}</div>, 'align-top');
    }

    private renderField = (name: string, valueElement: JSX.Element, className?: string): JSX.Element | null => {
        return (
            <div className={`item align-center ${className || ''}`}>
                {!!name && <div className="label">{name}</div>}
                {valueElement}
            </div>
        );
    }
}

function mapStateToProps(state: ApplicationState, ownProps: OwnProps): StateProps {
    return {
        baseResourceCalendar: getEntityCalendar(state.calendars, ownProps.resource, ownProps.resource.calendar.id),
        resourcePlanningLevel: mapServerEntityType[state.tenant.resourcePlanningSettings?.resourcePlanningLevel!]!,
        tenant: state.tenant,
        user: state.user,
        defaultCostRateTableId: getDefaultCostRateTable(state)?.id,
    };
}

export default connect(mapStateToProps)(UtilizationTooltipContent);